import { useEffect, useId, useRef, useState } from 'react';
import { publicJourneyApi } from '../../api/publicJourneyApi';
import { useTravelText } from '../../app/TravelLocaleContext';
import { useMemberSafetyDialogA11y } from '../group-trip/memberSafetyDialogA11y';
import { acquireStoryMediaSlot } from './storyMediaDownloadGate';
import {
  isStoryMediaAuthError,
  isStoryMediaCanceled,
  isStoryMediaRetryable,
  isStoryMediaSessionExpired,
  isStoryMediaUnavailable,
} from './storyMediaErrors';

const P = 'travel.publicJourney.story';

/**
 * 旅遊故事圖片檢視（同一張故事卡內切換）。
 *
 * - 取圖沿用授權端點 publicJourneyApi.getStoryMedia 與下載 gate；不使用公共代理／objectKey。
 * - 只保留目前一張 blob：切換、關閉、卸載時 abort 請求並 revoke URL；generation 忽略晚到回應。
 * - 以 mediaId 追蹤目前圖片；index 由呼叫端依 displayOrder 排好的清單推得。
 * - 語系切換不重新下載（effect 不依賴 locale）。
 */
export default function PublicJourneyStoryMediaViewer({
  publicationId,
  media,
  mediaId,
  cardTitle,
  onNavigate,
  onClose,
  onRequireAuth,
  returnFocusFallback,
}) {
  const { t, tf } = useTravelText();
  const titleId = useId();
  const panelRef = useRef(null);
  const objectUrlRef = useRef(null);
  const generationRef = useRef(0);
  const onCloseRef = useRef(onClose);
  const onRequireAuthRef = useRef(onRequireAuth);
  onCloseRef.current = onClose;
  onRequireAuthRef.current = onRequireAuth;
  // 狀態與 blob 都標記所屬 mediaId：切換後第一個 render 不會把上一張圖顯示在新的計數下
  const [status, setStatus] = useState({ mediaId: null, phase: 'loading' }); // loading | ready | unavailable | retryable
  const [loaded, setLoaded] = useState({ mediaId: null, url: null });
  const [retryToken, setRetryToken] = useState(0);
  const phase = status.mediaId === mediaId ? status.phase : 'loading';
  const objectUrl = loaded.mediaId === mediaId ? loaded.url : null;

  useMemberSafetyDialogA11y({ open: true, onClose, panelRef, returnFocusFallback });

  const list = Array.isArray(media) ? media : [];
  const index = list.findIndex((item) => item.mediaId === mediaId);
  const total = list.length;
  const position = index + 1;
  const prev = index > 0 ? list[index - 1] : null;
  const next = index >= 0 && index < total - 1 ? list[index + 1] : null;

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    const controller = new AbortController();
    let releaseSlot = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setLoaded({ mediaId: null, url: null });
    setStatus({ mediaId, phase: 'loading' });

    (async () => {
      try {
        releaseSlot = await acquireStoryMediaSlot();
        if (controller.signal.aborted || generation !== generationRef.current) return;
        const blob = await publicJourneyApi.getStoryMedia(publicationId, mediaId, { signal: controller.signal });
        if (controller.signal.aborted || generation !== generationRef.current) return;
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setLoaded({ mediaId, url });
        setStatus({ mediaId, phase: 'ready' });
      } catch (cause) {
        if (controller.signal.aborted || generation !== generationRef.current || isStoryMediaCanceled(cause)) return;
        if (isStoryMediaSessionExpired(cause)) {
          // app 已切成匿名：只關閉，不導向登入、不以匿名重試
          onCloseRef.current?.();
          return;
        }
        if (isStoryMediaAuthError(cause)) {
          onCloseRef.current?.();
          onRequireAuthRef.current?.();
          return;
        }
        if (isStoryMediaUnavailable(cause)) {
          setStatus({ mediaId, phase: 'unavailable' });
          return;
        }
        setStatus({ mediaId, phase: isStoryMediaRetryable(cause) ? 'retryable' : 'unavailable' });
      } finally {
        releaseSlot?.();
      }
    })();

    return () => {
      controller.abort();
      generationRef.current += 1;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [publicationId, mediaId, retryToken]);

  // 左右鍵於 document 層處理（同 Escape）：焦點暫時落在 body（例如重試鈕消失）時仍可切換
  const prevIdRef = useRef(null);
  const nextIdRef = useRef(null);
  const onNavigateRef = useRef(onNavigate);
  prevIdRef.current = prev?.mediaId ?? null;
  nextIdRef.current = next?.mediaId ?? null;
  onNavigateRef.current = onNavigate;
  useEffect(() => {
    function onKeyDown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey || event.defaultPrevented) return;
      if (event.key === 'ArrowLeft' && prevIdRef.current) {
        event.preventDefault();
        onNavigateRef.current?.(prevIdRef.current);
      } else if (event.key === 'ArrowRight' && nextIdRef.current) {
        event.preventDefault();
        onNavigateRef.current?.(nextIdRef.current);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  function retry() {
    // 重試鈕即將消失：先把焦點移到對話框本身，避免掉到 body
    panelRef.current?.focus();
    setRetryToken((value) => value + 1);
  }

  return (
    <div
      className="journey-story-viewer__scrim"
      data-testid="public-journey-story-viewer-scrim"
      role="presentation"
      onClick={() => onCloseRef.current?.()}
    >
      <div
        ref={panelRef}
        className="journey-story-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-testid="public-journey-story-viewer"
        data-media-id={mediaId}
        data-phase={phase}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="journey-story-viewer__header">
          <div className="journey-story-viewer__heading">
            <h2 id={titleId}>{tf(`${P}.viewer.dialog`, { index: position, total })}</h2>
            {cardTitle ? <p className="journey-story-viewer__card-title">{cardTitle}</p> : null}
          </div>
          <button
            type="button"
            className="journey-story-viewer__close"
            data-testid="public-journey-story-viewer-close"
            onClick={() => onCloseRef.current?.()}
          >
            {t(`${P}.viewer.close`)}
          </button>
        </div>
        <div className="journey-story-viewer__body" data-testid="public-journey-story-viewer-body" aria-busy={phase === 'loading'}>
          {phase === 'ready' && objectUrl ? (
            <img
              className="journey-story-viewer__img"
              src={objectUrl}
              alt={tf(`${P}.media.alt`, { index: position, total })}
            />
          ) : null}
          {phase === 'loading' ? <p className="journey-story-viewer__status">{t(`${P}.media.loading`)}</p> : null}
          {phase === 'unavailable' ? (
            <p className="journey-story-viewer__status" role="status">{t(`${P}.media.unavailable`)}</p>
          ) : null}
          {phase === 'retryable' ? (
            <div className="journey-story-viewer__retry">
              <p className="journey-story-viewer__status" role="status">{t(`${P}.media.retryable`)}</p>
              <button
                type="button"
                className="journey-story__media-retry-btn"
                onClick={retry}
              >
                {t(`${P}.media.retry`)}
              </button>
            </div>
          ) : null}
        </div>
        <div className="journey-story-viewer__nav">
          <button
            type="button"
            className="journey-story-viewer__step"
            data-testid="public-journey-story-viewer-prev"
            disabled={!prev}
            onClick={() => prev && onNavigate(prev.mediaId)}
          >
            {t(`${P}.viewer.prev`)}
          </button>
          <span className="journey-story-viewer__counter" aria-hidden="true">
            {tf(`${P}.viewer.counter`, { index: position, total })}
          </span>
          <button
            type="button"
            className="journey-story-viewer__step"
            data-testid="public-journey-story-viewer-next"
            disabled={!next}
            onClick={() => next && onNavigate(next.mediaId)}
          >
            {t(`${P}.viewer.next`)}
          </button>
        </div>
      </div>
    </div>
  );
}
